import React from 'react';

/**
 * IEC 60617 Standart Elektrik Şematik Sembolleri
 */

export interface SymbolPinProps {
  label?: string | undefined;
  cx: number;
  cy: number;
  isEnergized?: boolean | undefined;
  isSelected?: boolean | undefined;
  onClick?: ((e: React.MouseEvent) => void) | undefined;
  title?: string | undefined;
}

export const SchematicPin: React.FC<SymbolPinProps> = ({
  label,
  cx,
  cy,
  isEnergized = false,
  isSelected = false,
  onClick,
  title,
}) => (
  <g
    transform={`translate(${cx}, ${cy})`}
    style={{ cursor: onClick ? 'pointer' : 'default', pointerEvents: 'auto' }}
    onClick={(e) => {
      if (onClick) {
        e.stopPropagation();
        onClick(e);
      }
    }}
  >
    {title && <title>{title}</title>}
    <circle
      cx="0"
      cy="0"
      r={isSelected ? 5.5 : 3.5}
      fill={isEnergized ? '#f59e0b' : '#1e293b'}
      stroke={isSelected ? '#38bdf8' : isEnergized ? '#fbbf24' : '#64748b'}
      strokeWidth={isSelected ? 2 : 1}
    />
    {isSelected && (
      <circle cx="0" cy="0" r="8" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="2,2" />
    )}
    {label && (
      <text
        x="0"
        y="-6"
        fill={isSelected ? '#38bdf8' : '#94a3b8'}
        fontSize="6.5"
        fontFamily="monospace"
        fontWeight="bold"
        textAnchor="middle"
      >
        {label}
      </text>
    )}
  </g>
);

/**
 * Kontaktör Bobini Sembolü (IEC 60617 - Dikdörtgen)
 */
export const CoilSymbol: React.FC<{
  designator: string;
  isEnergized?: boolean | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ designator, isEnergized = false, x, y, onPinClick, activePinId, pinTitles }) => (
  <g transform={`translate(${x}, ${y})`}>
    {/* Bobin Dikdörtgeni */}
    <rect
      x="-14"
      y="-18"
      width="28"
      height="36"
      fill={isEnergized ? 'rgba(245, 158, 11, 0.15)' : '#1e293b'}
      stroke={isEnergized ? '#f59e0b' : '#94a3b8'}
      strokeWidth={isEnergized ? '2' : '1.4'}
      rx="2"
    />
    {/* Üst ve Alt Bağlantı Hatları */}
    <line x1="0" y1="-28" x2="0" y2="-18" stroke={isEnergized ? '#f59e0b' : '#94a3b8'} strokeWidth="1.5" />
    <line x1="0" y1="18" x2="0" y2="28" stroke={isEnergized ? '#f59e0b' : '#94a3b8'} strokeWidth="1.5" />

    {/* Klemens İsimleri: A1 ve A2 */}
    <SchematicPin
      label="A1"
      cx={0}
      cy={-28}
      isEnergized={isEnergized}
      isSelected={activePinId === 'A1'}
      onClick={onPinClick ? () => onPinClick('A1') : undefined}
      title={pinTitles?.['A1']}
    />
    <SchematicPin
      label="A2"
      cx={0}
      cy={28}
      isEnergized={isEnergized}
      isSelected={activePinId === 'A2'}
      onClick={onPinClick ? () => onPinClick('A2') : undefined}
      title={pinTitles?.['A2']}
    />

    {/* Cihaz İsmi */}
    <text
      x="0"
      y="3"
      fill={isEnergized ? '#f59e0b' : '#f8fafc'}
      fontSize="9"
      fontFamily="sans-serif"
      fontWeight="bold"
      textAnchor="middle"
    >
      {designator}
    </text>
  </g>
);

/**
 * Normalde Açık Kontak Sembolü (NO Contact - IEC 60617)
 */
export const NoContactSymbol: React.FC<{
  labelFrom: string;
  labelTo: string;
  designator?: string | undefined;
  isClosed?: boolean | undefined;
  isEnergized?: boolean | undefined;
  x: number;
  y: number;
  vertical?: boolean | undefined;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({
  labelFrom,
  labelTo,
  designator,
  isClosed = false,
  isEnergized = false,
  x,
  y,
  vertical = true,
  onPinClick,
  activePinId,
  pinTitles,
}) => {
  const strokeColor = isEnergized ? '#f59e0b' : '#94a3b8';
  const strokeW = isEnergized ? 2 : 1.5;

  if (vertical) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        {/* Üst ve Alt Hatlar */}
        <line x1="0" y1="-20" x2="0" y2="-8" stroke={strokeColor} strokeWidth={strokeW} />
        <line x1="0" y1="8" x2="0" y2="20" stroke={strokeColor} strokeWidth={strokeW} />

        {/* Hareketli Kontak Bacağı */}
        <line
          x1="-1"
          y1="8"
          x2={isClosed ? '0' : '10'}
          y2={isClosed ? '-8' : '-9'}
          stroke={strokeColor}
          strokeWidth={strokeW + 0.3}
          style={{ transition: 'all 0.1s ease' }}
        />

        <SchematicPin
          label={labelFrom}
          cx={0}
          cy={-20}
          isEnergized={isEnergized}
          isSelected={activePinId === labelFrom}
          onClick={onPinClick ? () => onPinClick(labelFrom) : undefined}
          title={pinTitles?.[labelFrom]}
        />
        <SchematicPin
          label={labelTo}
          cx={0}
          cy={20}
          isEnergized={isEnergized && isClosed}
          isSelected={activePinId === labelTo}
          onClick={onPinClick ? () => onPinClick(labelTo) : undefined}
          title={pinTitles?.[labelTo]}
        />

        {designator && (
          <text x="14" y="2" fill="#cbd5e1" fontSize="8" fontWeight="bold">
            {designator}
          </text>
        )}
      </g>
    );
  }

  // Yatay NO Kontak
  return (
    <g transform={`translate(${x}, ${y})`}>
      <line x1="-20" y1="0" x2="-8" y2="0" stroke={strokeColor} strokeWidth={strokeW} />
      <line x1="8" y1="0" x2="20" y2="0" stroke={strokeColor} strokeWidth={strokeW} />
      <line
        x1="-8"
        y1="-1"
        x2={isClosed ? '8' : '7'}
        y2={isClosed ? '0' : '-10'}
        stroke={strokeColor}
        strokeWidth={strokeW + 0.3}
      />
      <SchematicPin
        label={labelFrom}
        cx={-20}
        cy={0}
        isEnergized={isEnergized}
        isSelected={activePinId === labelFrom}
        onClick={onPinClick ? () => onPinClick(labelFrom) : undefined}
        title={pinTitles?.[labelFrom]}
      />
      <SchematicPin
        label={labelTo}
        cx={20}
        cy={0}
        isEnergized={isEnergized && isClosed}
        isSelected={activePinId === labelTo}
        onClick={onPinClick ? () => onPinClick(labelTo) : undefined}
        title={pinTitles?.[labelTo]}
      />
      {designator && (
        <text x="0" y="-12" fill="#cbd5e1" fontSize="8" fontWeight="bold" textAnchor="middle">
          {designator}
        </text>
      )}
    </g>
  );
};

/**
 * Normalde Kapalı Kontak Sembolü (NC Contact - IEC 60617)
 */
export const NcContactSymbol: React.FC<{
  labelFrom: string;
  labelTo: string;
  designator?: string | undefined;
  isOpen?: boolean | undefined;
  isEnergized?: boolean | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ labelFrom, labelTo, designator, isOpen = false, isEnergized = false, x, y, onPinClick, activePinId, pinTitles }) => {
  const strokeColor = isEnergized ? '#f59e0b' : '#94a3b8';
  const strokeW = isEnergized ? 2 : 1.5;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <line x1="0" y1="-20" x2="0" y2="-8" stroke={strokeColor} strokeWidth={strokeW} />
      <line x1="0" y1="8" x2="0" y2="20" stroke={strokeColor} strokeWidth={strokeW} />

      {/* NC Kontak Eğik Kapatma Bacağı */}
      <line
        x1="-1"
        y1="8"
        x2={isOpen ? '10' : '0'}
        y2={isOpen ? '-9' : '-8'}
        stroke={strokeColor}
        strokeWidth={strokeW + 0.3}
        style={{ transition: 'all 0.1s ease' }}
      />
      {/* NC kontak alt köprü çentiği */}
      <line x1="-4" y1="-8" x2="4" y2="-8" stroke={strokeColor} strokeWidth="1.2" />

      <SchematicPin
        label={labelFrom}
        cx={0}
        cy={-20}
        isEnergized={isEnergized}
        isSelected={activePinId === labelFrom}
        onClick={onPinClick ? () => onPinClick(labelFrom) : undefined}
        title={pinTitles?.[labelFrom]}
      />
      <SchematicPin
        label={labelTo}
        cx={0}
        cy={20}
        isEnergized={isEnergized && !isOpen}
        isSelected={activePinId === labelTo}
        onClick={onPinClick ? () => onPinClick(labelTo) : undefined}
        title={pinTitles?.[labelTo]}
      />

      {designator && (
        <text x="14" y="2" fill="#cbd5e1" fontSize="8" fontWeight="bold">
          {designator}
        </text>
      )}
    </g>
  );
};

/**
 * 3 Kutuplu Güç Kontağı Sembolü (KM1 Güç Devresi)
 */
export const PowerContacts3PSymbol: React.FC<{
  designator: string;
  isClosed?: boolean | undefined;
  isEnergized?: boolean | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ designator, isClosed = false, isEnergized = false, x, y, onPinClick, activePinId, pinTitles }) => {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* 3 Kutup Yan Yana (Fazlar: L1, L2, L3) */}
      <NoContactSymbol
        labelFrom="1"
        labelTo="2"
        isClosed={isClosed}
        isEnergized={isEnergized}
        x={0}
        y={0}
        onPinClick={onPinClick}
        activePinId={activePinId}
        pinTitles={pinTitles}
      />
      <NoContactSymbol
        labelFrom="3"
        labelTo="4"
        isClosed={isClosed}
        isEnergized={isEnergized}
        x={28}
        y={0}
        onPinClick={onPinClick}
        activePinId={activePinId}
        pinTitles={pinTitles}
      />
      <NoContactSymbol
        labelFrom="5"
        labelTo="6"
        isClosed={isClosed}
        isEnergized={isEnergized}
        x={56}
        y={0}
        onPinClick={onPinClick}
        activePinId={activePinId}
        pinTitles={pinTitles}
      />

      {/* 3 kutbu birbirine bağlayan kesikli mekanik çizgi */}
      <line x1="5" y1="0" x2="61" y2="0" stroke="#64748b" strokeWidth="1" strokeDasharray="3,2" />

      {/* Cihaz Etiketi */}
      <text x="-16" y="2" fill="#38bdf8" fontSize="8" fontWeight="bold">
        {designator}
      </text>
    </g>
  );
};

/**
 * Termik Röle Bimetal Isıtıcı Sembolü (FR1 Güç Devresi)
 */
export const ThermalHeaters3PSymbol: React.FC<{
  designator: string;
  isEnergized?: boolean | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ designator, isEnergized = false, x, y, onPinClick, activePinId, pinTitles }) => {
  const strokeColor = isEnergized ? '#f59e0b' : '#94a3b8';

  const renderBimetal = (bx: number, inLabel: string, outLabel: string) => (
    <g key={inLabel} transform={`translate(${bx}, 0)`}>
      <line x1="0" y1="-18" x2="0" y2="-9" stroke={strokeColor} strokeWidth="1.6" />
      {/* Bimetal U Şeklinde Isıtıcı Eleman */}
      <path
        d="M 0 -9 C 8 -9, 8 9, 0 9"
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.8"
      />
      <line x1="0" y1="9" x2="0" y2="18" stroke={strokeColor} strokeWidth="1.6" />
      <SchematicPin
        label={inLabel}
        cx={0}
        cy={-18}
        isEnergized={isEnergized}
        isSelected={activePinId === inLabel}
        onClick={onPinClick ? () => onPinClick(inLabel) : undefined}
        title={pinTitles?.[inLabel]}
      />
      <SchematicPin
        label={outLabel}
        cx={0}
        cy={18}
        isEnergized={isEnergized}
        isSelected={activePinId === outLabel}
        onClick={onPinClick ? () => onPinClick(outLabel) : undefined}
        title={pinTitles?.[outLabel]}
      />
    </g>
  );

  return (
    <g transform={`translate(${x}, ${y})`}>
      {renderBimetal(0, '1', '2')}
      {renderBimetal(28, '3', '4')}
      {renderBimetal(56, '5', '6')}

      {/* Mekanik Bağlantı Kesikli Hattı */}
      <line x1="4" y1="0" x2="60" y2="0" stroke="#ef4444" strokeWidth="1" strokeDasharray="3,2" />

      {/* Cihaz Etiketi */}
      <text x="-16" y="2" fill="#ef4444" fontSize="8" fontWeight="bold">
        {designator}
      </text>
    </g>
  );
};

/**
 * 3 Fazlı Asenkron Motor Sembolü (M 3~ IEC 60617)
 */
export const MotorSchematicSymbol: React.FC<{
  designator: string;
  isRunning?: boolean | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ designator, isRunning = false, x, y, onPinClick, activePinId, pinTitles }) => {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Motor Çemberi */}
      <circle
        cx="28"
        cy="24"
        r="24"
        fill={isRunning ? 'rgba(34, 197, 94, 0.15)' : '#1e293b'}
        stroke={isRunning ? '#22c55e' : '#64748b'}
        strokeWidth={isRunning ? '2.5' : '1.8'}
      />

      {/* 3 Giriş Faz Hattı (U1, V1, W1) */}
      <line x1="14" y1="0" x2="14" y2="-16" stroke="#94a3b8" strokeWidth="1.5" />
      <line x1="28" y1="0" x2="28" y2="-16" stroke="#94a3b8" strokeWidth="1.5" />
      <line x1="42" y1="0" x2="42" y2="-16" stroke="#94a3b8" strokeWidth="1.5" />

      <SchematicPin
        label="U1"
        cx={14}
        cy={-16}
        isEnergized={isRunning}
        isSelected={activePinId === 'U1'}
        onClick={onPinClick ? () => onPinClick('U1') : undefined}
        title={pinTitles?.['U1']}
      />
      <SchematicPin
        label="V1"
        cx={28}
        cy={-16}
        isEnergized={isRunning}
        isSelected={activePinId === 'V1'}
        onClick={onPinClick ? () => onPinClick('V1') : undefined}
        title={pinTitles?.['V1']}
      />
      <SchematicPin
        label="W1"
        cx={42}
        cy={-16}
        isEnergized={isRunning}
        isSelected={activePinId === 'W1'}
        onClick={onPinClick ? () => onPinClick('W1') : undefined}
        title={pinTitles?.['W1']}
      />

      {/* Topraklama (PE) */}
      <line x1="52" y1="24" x2="64" y2="24" stroke="#84cc16" strokeWidth="1.4" />
      <SchematicPin
        label="PE"
        cx={64}
        cy={24}
        isEnergized={false}
        isSelected={activePinId === 'PE'}
        onClick={onPinClick ? () => onPinClick('PE') : undefined}
        title={pinTitles?.['PE']}
      />

      {/* Motor Metinleri */}
      <text
        x="28"
        y="21"
        fill="#f8fafc"
        fontSize="11"
        fontFamily="sans-serif"
        fontWeight="bold"
        textAnchor="middle"
      >
        M
      </text>
      <text
        x="28"
        y="32"
        fill={isRunning ? '#4ade80' : '#94a3b8'}
        fontSize="8"
        fontFamily="sans-serif"
        fontWeight="bold"
        textAnchor="middle"
      >
        3 ~
      </text>

      <text x="-14" y="26" fill="#38bdf8" fontSize="8.5" fontWeight="bold">
        {designator}
      </text>
    </g>
  );
};

/**
 * Sinyal Lambası Sembolü (Çember içinde X - IEC 60617)
 */
export const LampSchematicSymbol: React.FC<{
  designator: string;
  isLit?: boolean | undefined;
  color?: string | undefined;
  x: number;
  y: number;
  onPinClick?: ((pinId: string) => void) | undefined;
  activePinId?: string | null | undefined;
  pinTitles?: Record<string, string> | undefined;
}> = ({ designator, isLit = false, color = '#22c55e', x, y, onPinClick, activePinId, pinTitles }) => {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <line x1="0" y1="-24" x2="0" y2="-14" stroke="#94a3b8" strokeWidth="1.5" />
      <line x1="0" y1="14" x2="0" y2="24" stroke="#94a3b8" strokeWidth="1.5" />

      {/* Çember ve Çapraz Çizgiler */}
      <circle
        cx="0"
        cy="0"
        r="14"
        fill={isLit ? color : '#1e293b'}
        fillOpacity={isLit ? 0.3 : 1}
        stroke={isLit ? color : '#94a3b8'}
        strokeWidth={isLit ? '2' : '1.4'}
      />
      <line x1="-10" y1="-10" x2="10" y2="10" stroke={isLit ? color : '#94a3b8'} strokeWidth="1.4" />
      <line x1="10" y1="-10" x2="-10" y2="10" stroke={isLit ? color : '#94a3b8'} strokeWidth="1.4" />

      <SchematicPin
        label="X1"
        cx={0}
        cy={-24}
        isEnergized={isLit}
        isSelected={activePinId === 'X1'}
        onClick={onPinClick ? () => onPinClick('X1') : undefined}
        title={pinTitles?.['X1']}
      />
      <SchematicPin
        label="X2"
        cx={0}
        cy={24}
        isEnergized={isLit}
        isSelected={activePinId === 'X2'}
        onClick={onPinClick ? () => onPinClick('X2') : undefined}
        title={pinTitles?.['X2']}
      />

      <text x="18" y="3" fill="#cbd5e1" fontSize="8" fontWeight="bold">
        {designator}
      </text>
    </g>
  );
};
